import { useState, useEffect } from "react";
import { DollarSign, Calendar, Download, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PayrollSection() {
  const { api } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api().get(`/payroll?month=${month}&year=${year}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, [api, month, year]);

  const totalPay = data?.payroll?.reduce((s, p) => s + p.total_pay, 0) || 0;

  return (
    <div className="space-y-6" data-testid="payroll-section">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-navy">Payroll</h2>
          <p className="text-slate-400 text-sm font-sans mt-1">Monthly salary calculation based on attendance</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="form-input w-auto" data-testid="payroll-month">
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="form-input w-auto" data-testid="payroll-year">
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="card-dashboard text-center">
          <p className="text-slate-400 text-sm font-sans">Total Employees</p>
          <p className="text-3xl font-bold text-navy font-mono mt-1">{data?.payroll?.length || 0}</p>
        </div>
        <div className="card-dashboard text-center">
          <p className="text-slate-400 text-sm font-sans">Working Days</p>
          <p className="text-3xl font-bold text-forest font-mono mt-1">{data?.total_working_days || 0}</p>
        </div>
        <div className="card-dashboard text-center">
          <p className="text-slate-400 text-sm font-sans">Total Payroll</p>
          <p className="text-3xl font-bold text-coir font-mono mt-1">Rs.{totalPay.toLocaleString()}</p>
        </div>
      </div>

      <div className="card-dashboard">
        {loading ? <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-navy/20 border-t-navy rounded-full animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="payroll-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Employee</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Salary Type</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Pay Rate</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Full Days</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Half Days</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Leave</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Absent</th>
                  <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans text-right">Total Pay</th>
                </tr>
              </thead>
              <tbody>
                {data?.payroll?.map(row => (
                  <tr key={row.employee.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`payroll-row-${row.employee.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center"><span className="text-white text-xs font-bold">{row.employee.name.split(" ").map(n => n[0]).join("").slice(0, 2)}</span></div>
                        <div>
                          <p className="font-medium text-navy text-sm font-sans">{row.employee.name}</p>
                          <p className="text-xs text-slate-400 font-sans">{row.employee.department}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3"><span className={`px-2 py-1 text-xs font-medium font-sans ${row.employee.salary_type === "daily_wage" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{row.employee.salary_type === "daily_wage" ? "Daily" : "Monthly"}</span></td>
                    <td className="p-3 font-mono text-sm">Rs.{(row.employee.pay_rate || 0).toLocaleString()}</td>
                    <td className="p-3 font-mono text-sm text-green-600">{row.full_days}</td>
                    <td className="p-3 font-mono text-sm text-yellow-600">{row.half_days}</td>
                    <td className="p-3 font-mono text-sm text-blue-600">{row.leaves}</td>
                    <td className="p-3 font-mono text-sm text-red-600">{row.absents}</td>
                    <td className="p-3 text-right font-mono text-sm font-bold text-navy">Rs.{row.total_pay.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              {data?.payroll?.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-navy">
                    <td colSpan="7" className="p-3 font-medium text-navy font-sans text-right">Total Payroll</td>
                    <td className="p-3 text-right font-mono font-bold text-navy text-lg">Rs.{totalPay.toLocaleString()}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
